import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, UploadSimple, LinkSimple, Plus, Minus, Check } from '@phosphor-icons/react';
import API from '../lib/api';
import { useAuth } from '../lib/auth';

/** Applies a single variant option to a running price. */
const applyOption = (price, opt) => {
  if (!opt) return price;
  const v = Number(opt.value) || 0;
  if (opt.mode === 'replace') return v;
  switch (opt.operator) {
    case '+': return price + v;
    case '-': return price - v;
    case '*': return price * (v || 1);
    case '/': return v ? price / v : price;
    default: return price + v;
  }
};

const opLabel = (opt) => {
  if (!opt) return '';
  if (opt.mode === 'replace') return `= ₹${opt.value}`;
  const v = opt.value;
  if (!v) return '';
  if (opt.operator === '*') return `× ${v}`;
  if (opt.operator === '/') return `÷ ${v}`;
  if (opt.operator === '-') return `− ₹${v}`;
  return `+ ₹${v}`;
};

const ProductDetail = () => {
  const { pid } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();

  const [product, setProduct] = useState(null);
  // selectedVariants map { [groupId]: optionObj }
  const [selectedVariants, setSelectedVariants] = useState({});
  // Track the most-recently-clicked group so its image wins the hero swap.
  const [lastPickedGroupId, setLastPickedGroupId] = useState(null);
  // Carousel index + manual override flag (set true when user clicks dots/thumbs,
  // reset when user picks a variant so its image swaps in).
  const [imgIdx, setImgIdx] = useState(0);
  const [manualImg, setManualImg] = useState(false);
  // Carousel dots auto-hide after 2s of no interaction on the hero.
  const [dotsVisible, setDotsVisible] = useState(true);
  const dotsHideTimer = useRef(null);
  const touchStartRef = useRef(null);
  // Legacy state (kept for backward compat with products that still use material_options/size_options)
  const [material, setMaterial] = useState(null);
  const [size, setSize] = useState(null);
  const [lamination, setLamination] = useState(false);
  const [laminationOpt, setLaminationOpt] = useState(null);

  const [qty, setQty] = useState(1);
  const [width, setWidth] = useState(4);
  const [height, setHeight] = useState(6);
  const [file, setFile] = useState(null);
  const [fileMode, setFileMode] = useState('upload');
  const [link, setLink] = useState('');
  const [colorProfile, setColorProfile] = useState(null);

  // Sticky-collapsing hero: when the sentinel (placed just under the description)
  // scrolls out of view, we collapse the hero into a compact top bar so users can
  // still see the selected variant's image + product name while browsing options.
  const [collapsed, setCollapsed] = useState(false);
  const sentinelRef = useRef(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !product) return;
    // Find the ancestor that actually scrolls (MobileFrame's overflow-y-auto).
    let scrollParent = el.parentElement;
    while (scrollParent && scrollParent !== document.body) {
      const s = getComputedStyle(scrollParent);
      if (/(auto|scroll)/.test(s.overflowY)) break;
      scrollParent = scrollParent.parentElement;
    }
    const io = new IntersectionObserver(
      ([entry]) => setCollapsed(!entry.isIntersecting),
      { root: scrollParent === document.body ? null : scrollParent, threshold: 0 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [product]);

  useEffect(() => {
    API.get(`/catalog/${pid}`).then(r => {
      const p = r.data;
      setProduct(p);
      setQty(p.min_qty || 1);
      // No pre-selection — customer must click a variant option to see it activate.
      setSelectedVariants({});
      setLastPickedGroupId(null);
      // legacy fallbacks — the legacy code path still expects a preselected material/size
      // to keep the old pill UI functional for products that predate variant_groups.
      if (p.material_options?.length) setMaterial(p.material_options[0]);
      if (p.size_options?.length) setSize(p.size_options[0]);
    }).catch(() => nav('/categories'));
  }, [pid, nav]);

  useEffect(() => {
    if (product?.lamination_options?.length) setLaminationOpt(product.lamination_options[0]);
  }, [product]);

  // Reveal carousel dots on image change; auto-hide after 2s of no interaction.
  useEffect(() => {
    setDotsVisible(true);
    if (dotsHideTimer.current) clearTimeout(dotsHideTimer.current);
    dotsHideTimer.current = setTimeout(() => setDotsVisible(false), 2000);
    return () => { if (dotsHideTimer.current) clearTimeout(dotsHideTimer.current); };
  }, [imgIdx, manualImg]);

  // Central login-gate: any configuration action for anonymous users routes them to /login
  // with a return-URL, then toasts. Returns `true` if navigation happened (caller should bail).
  const requireLogin = () => {
    if (user) return false;
    toast.error('Please login to configure and order');
    nav('/login', { state: { from: `/shop/${pid}` } });
    return true;
  };

  const usesVariantGroups = (product?.variant_groups?.length || 0) > 0;

  // Build image list: last-selected variant image (if any) takes priority, then carousel `images`, then main `image`.
  const carouselImages = useMemo(() => {
    if (!product) return [];
    const list = (product.images && product.images.length) ? [...product.images] : (product.image ? [product.image] : []);
    return list;
  }, [product]);

  // Currently displayed hero image:
  //  1. If user just clicked a carousel dot/thumb → carousel[imgIdx] wins
  //  2. Else, the MOST-RECENTLY-picked variant option's image wins (so Group-2
  //     overrides Group-1 when the user clicks Group-2 after Group-1).
  //  3. Fallback: carousel[imgIdx] or product.image.
  const heroImage = useMemo(() => {
    if (manualImg) return carouselImages[imgIdx] || product?.image;
    if (usesVariantGroups && lastPickedGroupId) {
      const sel = selectedVariants[lastPickedGroupId];
      if (sel?.image) return sel.image;
    }
    // Legacy: material image
    if (!usesVariantGroups && material?.image) return material.image;
    return carouselImages[imgIdx] || product?.image;
  }, [product, selectedVariants, carouselImages, imgIdx, material, usesVariantGroups, manualImg, lastPickedGroupId]);

  const { unitPrice, sqft, billableSqft, total } = useMemo(() => {
    if (!product) return { unitPrice: 0, sqft: 0, billableSqft: 0, total: 0 };
    const minSqft = Number(product.min_sqft) > 0 ? Number(product.min_sqft) : 0;

    // Modern path: variant_groups
    if (usesVariantGroups) {
      let unit = product.base_price || 0;
      // Sort so replace ops apply first, then modifiers — otherwise a replace after a modifier wipes it out.
      const opts = Object.values(selectedVariants).filter(Boolean);
      const replaceOpts = opts.filter(o => o.mode === 'replace');
      const modOpts = opts.filter(o => o.mode !== 'replace');
      replaceOpts.forEach(o => { unit = applyOption(unit, o); });
      modOpts.forEach(o => { unit = applyOption(unit, o); });
      unit = Math.max(0, unit);
      if (product.pricing_mode === 'per_sqft') {
        const sq = (width || 0) * (height || 0);
        const bill = Math.max(sq * qty, minSqft);
        return { unitPrice: unit, sqft: sq, billableSqft: bill, total: Math.round(unit * bill) };
      }
      return { unitPrice: unit, sqft: 0, billableSqft: 0, total: Math.round(unit * qty) };
    }

    // Legacy path
    const surcharge = (material?.surcharge || 0);
    const laminationExtra = (product.lamination_options?.length)
      ? (laminationOpt?.surcharge || 0)
      : (lamination ? (product.lamination_surcharge || 0) : 0);
    let unit = 0, sq = 0;
    if (product.pricing_mode === 'per_sqft') {
      sq = (width || 0) * (height || 0);
      unit = (product.base_price + surcharge + laminationExtra);
      const bill = Math.max(sq * qty, minSqft);
      return { unitPrice: unit, sqft: sq, billableSqft: bill, total: Math.round(unit * bill) };
    }
    if (product.pricing_mode === 'per_unit') {
      const mult = size?.multiplier || 1;
      unit = (product.base_price + surcharge + laminationExtra) * mult;
      return { unitPrice: unit, sqft: 0, billableSqft: 0, total: Math.round(unit * qty) };
    }
    if (product.pricing_mode === 'fixed') {
      unit = product.base_price + surcharge;
      return { unitPrice: unit, sqft: 0, billableSqft: 0, total: Math.round(unit * qty) };
    }
    return { unitPrice: 0, sqft: 0, billableSqft: 0, total: 0 };
  }, [product, usesVariantGroups, selectedVariants, material, size, qty, width, height, lamination, laminationOpt]);

  const handleUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!user) { toast.error('Please login first'); nav('/login'); return; }
    const fd = new FormData();
    fd.append('file', f);
    try {
      const r = await API.post('/files/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setFile({ file_id: r.data.file_id, name: r.data.name });
      toast.success('File uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    }
  };

  const proceed = async () => {
    if (!user) { toast.error('Please login to order'); nav('/login', { state: { from: `/shop/${pid}` } }); return; }
    if (product.pricing_mode === 'callback') {
      try {
        const parts = usesVariantGroups
          ? Object.values(selectedVariants).filter(Boolean).map(o => o.label).join(' · ')
          : (material?.label || '');
        const msg = `Callback request for "${product.name}" · qty ${qty}${parts ? ' · ' + parts : ''}`;
        await API.post('/support/tickets', { type: 'callback_request', phone: user.phone || '', message: msg });
        toast.success('Callback requested — our team will reach out soon.');
        nav('/support');
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Could not submit request');
      }
      return;
    }
    if (!file?.file_id && !link) { toast.error('Please upload a file or paste a link'); return; }
    if (!file?.file_id && link) {
      try { const u = new URL(link); if (!/^https?:$/.test(u.protocol)) throw new Error('bad'); }
      catch { toast.error('Paste a valid link starting with https:// or http://'); return; }
    }

    // Require every "required" variant group to be selected before proceed.
    if (usesVariantGroups) {
      const missing = product.variant_groups.find(g => g.required && !selectedVariants[g.id]);
      if (missing) { toast.error(`Please select a ${missing.name.toLowerCase()}`); return; }
    }

    // Build variant_selections summary for the order item
    const variant_selections = usesVariantGroups
      ? Object.entries(selectedVariants).map(([gid, opt]) => {
          const group = product.variant_groups.find(g => g.id === gid);
          return { group: group?.name || gid, group_id: gid, option: opt.label, option_id: opt.id, image: opt.image || null };
        })
      : [];

    // Derive a short "material" / "size" label so partner-facing UI still works
    let materialLabel = null, sizeLabel = null, laminationLabel = null;
    if (usesVariantGroups) {
      product.variant_groups.forEach(g => {
        const sel = selectedVariants[g.id];
        if (!sel) return;
        const gname = (g.name || '').toLowerCase();
        if (gname.includes('size')) sizeLabel = sel.label;
        else if (gname.includes('lam')) laminationLabel = sel.label;
        else if (!materialLabel) materialLabel = sel.label;
      });
    } else {
      materialLabel = material?.label; sizeLabel = size?.label; laminationLabel = laminationOpt?.label;
    }

    const item = {
      product_id: product.product_id, product_name: product.name,
      quantity: qty, unit_price: unitPrice, total,
      material: materialLabel, size: sizeLabel,
      width: product.pricing_mode === 'per_sqft' ? width : null,
      height: product.pricing_mode === 'per_sqft' ? height : null,
      sqft: product.pricing_mode === 'per_sqft' ? sqft : null,
      color_profile: colorProfile, file_id: file?.file_id || null, file_link: link || null,
      required_machine_types: product.required_machine_types || [],
      category_id: product.category_id,
      lamination: usesVariantGroups
        ? Boolean(laminationLabel && laminationLabel.toLowerCase() !== 'no lamination' && laminationLabel.toLowerCase() !== 'none')
        : (product.lamination_options?.length ? (laminationOpt?.id !== 'none') : lamination),
      lamination_option: laminationLabel || null,
      variant_selections,
    };
    sessionStorage.setItem('pk_cart', JSON.stringify([item]));
    nav('/checkout');
  };

  if (!product) return <div className="p-8 text-center text-sm">Loading…</div>;

  const jumpImg = (i) => { setManualImg(true); setImgIdx(i); revealDots(); };

  // Show the dots and (re-)start the 2-second auto-hide timer.
  const revealDots = () => {
    setDotsVisible(true);
    if (dotsHideTimer.current) clearTimeout(dotsHideTimer.current);
    dotsHideTimer.current = setTimeout(() => setDotsVisible(false), 2000);
  };

  // Touch / drag swipe on the hero. Positive delta = swipe right = previous image.
  const onHeroPointerDown = (e) => {
    if (collapsed) return;
    const x = e.touches ? e.touches[0].clientX : e.clientX;
    touchStartRef.current = x;
    revealDots();
  };
  const onHeroPointerUp = (e) => {
    if (collapsed || touchStartRef.current == null) return;
    const endX = e.changedTouches ? e.changedTouches[0].clientX : e.clientX;
    const dx = endX - touchStartRef.current;
    touchStartRef.current = null;
    if (Math.abs(dx) < 40 || carouselImages.length < 2) return;
    const dir = dx < 0 ? 1 : -1;
    const next = (imgIdx + dir + carouselImages.length) % carouselImages.length;
    jumpImg(next);
  };

  return (
    <div className="pb-4">
      {/* --- Sticky collapsing hero: image + name stay visible while scrolling options --- */}
      <div className={`sticky top-0 z-30 bg-white transition-shadow ${collapsed ? 'shadow-[0_2px_0_#000]' : ''}`} data-testid="product-sticky-header" data-collapsed={collapsed}>
        {/* Hero — vertical 4:3 by default, collapses into a horizontal bar on scroll. Thumb keeps the same 4:3 aspect ratio. */}
        <div
          className={`relative bg-neutral-100 overflow-hidden transition-[height] duration-300 ${collapsed ? 'h-16 flex items-center gap-3 px-4' : 'aspect-[4/3] select-none touch-pan-y'}`}
          data-testid="product-carousel"
          onTouchStart={onHeroPointerDown}
          onTouchEnd={onHeroPointerUp}
          onPointerDown={(e) => { if (e.pointerType === 'mouse') onHeroPointerDown(e); }}
          onPointerUp={(e) => { if (e.pointerType === 'mouse') onHeroPointerUp(e); }}
        >
          {heroImage && (
            <img
              src={heroImage}
              alt={product.name}
              key={heroImage}
              draggable={false}
              className={`transition-all duration-300 object-cover pk-hero-fade ${collapsed ? 'w-16 h-12 shrink-0 border-2 border-black' : 'w-full h-full'}`}
            />
          )}
          {collapsed && (
            <div className="min-w-0 flex-1">
              <div className="text-[9px] tracking-[0.22em] uppercase font-bold text-neutral-500">{product.pricing_mode.replace('_', ' ')}</div>
              <div className="font-display text-[16px] leading-tight truncate">{product.name}</div>
            </div>
          )}
          <Link to="/categories" className={`pk-chip bg-white z-10 ${collapsed ? 'shrink-0' : 'absolute top-3 left-3'}`} data-testid="product-back"><ArrowLeft size={12} /> Back</Link>
          {!collapsed && carouselImages.length > 1 && (
            <div
              className={`absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 transition-opacity duration-300 ${dotsVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
              data-testid="carousel-dots"
              onMouseEnter={revealDots}
            >
              {carouselImages.map((src, i) => (
                <button
                  key={`${src}-${i}`}
                  onClick={() => jumpImg(i)}
                  className={`w-2 h-2 rounded-full border border-black transition-all ${i === imgIdx && manualImg ? 'bg-black w-4' : 'bg-white'}`}
                  data-testid={`carousel-dot-${i}`}
                  aria-label={`Show image ${i + 1}`}
                />
              ))}
            </div>
          )}
        </div>

        {!collapsed && carouselImages.length > 1 && (
          <div className="px-5 mt-3 flex gap-2 overflow-x-auto pb-1 justify-center" data-testid="carousel-thumbs">
            {carouselImages.map((src, i) => (
              <button
                key={`${src}-${i}`}
                onClick={() => jumpImg(i)}
                className={`shrink-0 w-14 h-14 border-2 overflow-hidden ${i === imgIdx && manualImg ? 'border-black brutal-shadow-sm' : 'border-neutral-300'}`}
                data-testid={`carousel-thumb-${i}`}
              >
                <img src={src} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Overline + product name (only shown when NOT collapsed — collapsed state shows name inside the sticky bar above) */}
      <div className="px-5 pt-4 reg-mark">
        <div className="text-[10px] tracking-[0.24em] uppercase font-bold" style={{ color: 'var(--pk-primary)' }}>— {product.pricing_mode.replace('_', ' ')}</div>
        <h1 className="font-display text-[28px] mt-1 leading-tight">{product.name}</h1>
        <p className="text-[13px] text-neutral-600 mt-1">{product.description}</p>
      </div>

      {/* Sentinel — as soon as it scrolls above the viewport, the sticky header collapses. */}
      <div ref={sentinelRef} className="h-px" aria-hidden />


      {/* --- Config --- */}
      <div className="px-5 mt-5 space-y-4">
        {/* NEW: variant_groups — card-style selectors */}
        {usesVariantGroups && product.variant_groups.map(group => {
          // Layout policy — admin can pick "grid" / "list", else auto-detect from content length.
          const layoutPref = group.layout || 'auto';
          const maxLabelLen = Math.max(0, ...group.options.map(o => (o.label || '').length));
          const anySublabel = group.options.some(o => (o.sublabel || '').trim());
          const anyLongLabel = group.options.some(o => (o.label || '').length > 12 || (o.sublabel || '').length > 22);
          // Compact = tiny pill grid (used for very short single-word options like A4/A3).
          const compact = layoutPref !== 'list' && maxLabelLen <= 4 && !anySublabel && group.options.length <= 5;
          // Force single-column list when admin picked "list" OR when auto detected long content.
          const useList = layoutPref === 'list' || (layoutPref === 'auto' && anyLongLabel);
          const gridClass = useList
            ? 'grid-cols-1'
            : compact
              ? {2: 'grid-cols-2', 3: 'grid-cols-3', 4: 'grid-cols-4', 5: 'grid-cols-5'}[group.options.length] || 'grid-cols-4'
              : 'grid-cols-2';
          return (
          <div key={group.id} data-testid={`vg-${group.id}`}>
            <div className="flex items-baseline justify-between mb-2">
              <h3 className="font-display text-[18px] leading-none">{group.name}{!group.required && <span className="text-neutral-400 font-normal text-[11px] ml-2 tracking-normal">optional</span>}</h3>
              {selectedVariants[group.id] && (
                <span className="text-[10px] font-mono text-neutral-500">{selectedVariants[group.id].label}</span>
              )}
            </div>
            <div className={`grid ${gridClass} gap-2`}>
              {group.options.map(opt => {
                const active = selectedVariants[group.id]?.id === opt.id;
                const v = Number(opt.value);
                // Hide "no-op" modifiers: +0, -0, ×1, ÷1
                const isNoop = opt.mode !== 'replace' && (
                  (opt.operator === '+' && v === 0) ||
                  (opt.operator === '-' && v === 0) ||
                  (opt.operator === '*' && v === 1) ||
                  (opt.operator === '/' && v === 1)
                );
                const showPrice = opt.mode !== 'replace' && !isNoop;
                return (
                  <button
                    key={opt.id}
                    onClick={() => {
                      if (requireLogin()) return;
                      setManualImg(false);
                      setLastPickedGroupId(group.id);
                      setSelectedVariants({ ...selectedVariants, [group.id]: opt });
                    }}
                    className={`variant-card relative border-2 transition-colors overflow-visible ${
                      compact
                        ? 'h-20 flex flex-col items-center justify-center p-2 text-center'
                        : useList
                          ? 'flex flex-row items-center gap-3 text-left p-3'
                          : 'flex flex-col items-start justify-between text-left p-3 min-h-[84px]'
                    } ${active ? 'border-[color:var(--pk-primary)]' : 'border-neutral-300 hover:border-neutral-500 bg-white'}`}
                    style={active ? { backgroundColor: 'color-mix(in srgb, var(--pk-primary) 10%, #ffffff)' } : undefined}
                    data-testid={`vg-${group.id}-${opt.id}`}
                    aria-pressed={active}
                  >
                    <div className={compact ? 'w-full' : 'flex-1 min-w-0'}>
                      <div className={`font-bold leading-tight ${compact ? 'text-[15px]' : 'text-[14px]'} ${active ? 'text-black' : 'text-neutral-800'}`}>{opt.label}</div>
                      {!compact && (opt.sublabel ? (
                        <div className={`text-[11px] text-neutral-500 leading-tight ${useList ? 'mt-0.5' : 'mt-1 min-h-[14px]'}`}>{opt.sublabel}</div>
                      ) : (
                        !useList && <div className="text-[11px] text-neutral-500 mt-1 leading-tight min-h-[14px]">&nbsp;</div>
                      ))}
                    </div>
                    {active && (
                      <span
                        className="absolute -top-2 -right-2 w-6 h-6 rounded-full flex items-center justify-center border-2 border-white shadow-md"
                        style={{ background: 'var(--pk-primary)' }}
                        aria-label="Selected"
                        data-testid={`vg-${group.id}-${opt.id}-check`}
                      >
                        <Check size={13} weight="bold" color="#fff" />
                      </span>
                    )}
                    {(showPrice || opt.mode === 'replace') ? (
                      <div className={`text-[10px] tracking-widest uppercase text-neutral-400 font-mono whitespace-nowrap ${
                        compact ? 'mt-1' : useList ? 'shrink-0' : 'mt-2 w-full'
                      }`}>
                        {opt.mode === 'replace' ? `₹${Number(opt.value).toLocaleString('en-IN')}` : opLabel(opt)}
                      </div>
                    ) : (
                      !compact && !useList && <div className="text-[10px] font-mono mt-2 w-full min-h-[12px]">&nbsp;</div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
          );
        })}

        {/* Legacy: material_options (only when there are no variant_groups) */}
        {!usesVariantGroups && product.material_options?.length > 0 && (
          <div>
            <div className="pk-label">Material</div>
            <div className="grid grid-cols-2 gap-2">
              {product.material_options.map(m => (
                <button key={m.id} onClick={() => { if (requireLogin()) return; setMaterial(m); }} className={`pk-chip justify-between ${material?.id === m.id ? 'active' : ''}`} data-testid={`material-${m.id}`}>
                  <span>{m.label}</span>
                  {m.surcharge ? <span className="font-mono">+₹{m.surcharge}</span> : null}
                </button>
              ))}
            </div>
          </div>
        )}

        {!usesVariantGroups && product.pricing_mode === 'per_unit' && product.size_options?.length > 0 && (
          <div>
            <div className="pk-label">Size</div>
            <div className="grid grid-cols-4 gap-2">
              {product.size_options.map(s => (
                <button key={s.id} onClick={() => { if (requireLogin()) return; setSize(s); }} className={`pk-chip justify-center ${size?.id === s.id ? 'active' : ''}`} data-testid={`size-${s.id}`}>{s.label}</button>
              ))}
            </div>
          </div>
        )}

        {product.pricing_mode === 'per_sqft' && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="pk-label">Width (ft)</div>
              <input type="number" min={0.5} step={0.5} className="pk-input font-mono" value={width} onChange={e => { if (requireLogin()) return; setWidth(parseFloat(e.target.value) || 0); }} data-testid="width-input" />
            </div>
            <div>
              <div className="pk-label">Height (ft)</div>
              <input type="number" min={0.5} step={0.5} className="pk-input font-mono" value={height} onChange={e => { if (requireLogin()) return; setHeight(parseFloat(e.target.value) || 0); }} data-testid="height-input" />
            </div>
            <div className="col-span-2 text-[11px] text-neutral-500 font-mono">
              Area = {sqft.toFixed(2)} sq.ft{qty > 1 ? ` × ${qty} = ${(sqft * qty).toFixed(2)} sq.ft` : ''}
              {Number(product.min_sqft) > 0 && (sqft * qty) < Number(product.min_sqft) && (
                <span className="ml-2 text-[10px] tracking-widest uppercase" style={{ color: 'var(--pk-primary)' }} data-testid="min-sqft-note">
                  Min {Number(product.min_sqft)} sq.ft — billing {billableSqft.toFixed(2)} sq.ft
                </span>
              )}
            </div>
          </div>
        )}

        {/* Upload artwork */}
        <div className="pk-card p-4" data-testid="upload-section">
          <div className="text-[10px] tracking-[0.24em] uppercase font-bold" style={{ color: 'var(--pk-primary)' }}>— Upload artwork</div>
          <div className="font-bold text-[16px] mt-1">Print file</div>
          <div className="text-[11px] text-neutral-600 mt-1">PDF, JPG or PNG. CMYK / 300 DPI recommended.</div>
          <div className="mt-3 flex gap-2">
            <button onClick={() => { if (requireLogin()) return; setFileMode('upload'); }} className={`pk-chip ${fileMode === 'upload' ? 'active' : ''}`} data-testid="file-tab-upload"><UploadSimple size={12}/> Upload</button>
            <button onClick={() => { if (requireLogin()) return; setFileMode('link'); }} className={`pk-chip ${fileMode === 'link' ? 'active' : ''}`} data-testid="file-tab-link"><LinkSimple size={12}/> Paste link</button>
          </div>
          {fileMode === 'upload' ? (
            <label className="mt-3 flex flex-col items-center justify-center py-6 pk-border border-dashed cursor-pointer hover:bg-yellow-50" onClick={(e) => { if (requireLogin()) { e.preventDefault(); } }}>
              <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={handleUpload} className="hidden" data-testid="file-upload-input" />
              <UploadSimple size={20} />
              <div className="font-bold text-[13px] mt-2">{file?.name || 'Tap to upload'}</div>
              <div className="text-[9px] tracking-[0.2em] uppercase text-neutral-500 mt-1">PDF · JPG · PNG</div>
            </label>
          ) : (
            <div>
              <input type="url" className="pk-input mt-3" placeholder="https://drive.google.com/... (must start with http/https)" value={link} onChange={e => { if (requireLogin()) return; setLink(e.target.value); }} data-testid="file-link-input" />
              {link && !/^https?:\/\/.+\..+/i.test(link) && (
                <div className="mt-1 text-[10px] font-bold" style={{ color: '#dc2626' }} data-testid="link-format-warning">
                  Link must start with http:// or https:// and be a valid URL.
                </div>
              )}
            </div>
          )}
          {(file || link) && (
            <div className="mt-4">
              <div className="pk-label">Color profile</div>
              <div className="flex gap-2" data-testid="color-profile-picker">
                {['cmyk','rgb','unsure'].map(cp => (
                  <button key={cp} onClick={() => { if (requireLogin()) return; setColorProfile(cp); }} className={`pk-chip flex-1 justify-center ${colorProfile === cp ? 'active' : ''}`} data-testid={`cp-${cp}`}>
                    {cp === 'cmyk' ? 'CMYK' : cp === 'rgb' ? 'RGB' : 'Not sure'}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div>
          <div className="pk-label">Quantity (min {product.min_qty})</div>
          <div className="flex items-center pk-border w-fit">
            <button onClick={() => { if (requireLogin()) return; setQty(Math.max(0, qty - (product.min_qty >= 25 ? 25 : 1))); }} className="w-11 h-11 flex items-center justify-center hover:bg-yellow-100" data-testid="qty-minus"><Minus size={14} /></button>
            <input type="text" inputMode="numeric" value={qty} onChange={e => { if (requireLogin()) return; const digits = e.target.value.replace(/\D/g, ''); setQty(digits === '' ? 0 : parseInt(digits, 10)); }} className="w-20 text-center font-mono border-x-2 border-black h-11 focus:outline-none" data-testid="qty-input" />
            <button onClick={() => { if (requireLogin()) return; setQty(qty + (product.min_qty >= 25 ? 25 : 1)); }} className="w-11 h-11 flex items-center justify-center hover:bg-yellow-100" data-testid="qty-plus"><Plus size={14} /></button>
          </div>
          {qty < (product.min_qty || 1) && (
            <div className="mt-1.5 text-[10px] tracking-wider font-bold" style={{ color: '#dc2626' }} data-testid="qty-moq-warning">
              Minimum order quantity is {product.min_qty}
            </div>
          )}
        </div>

        {/* Legacy lamination controls (only when no variant_groups) */}
        {!usesVariantGroups && (product.lamination_options?.length ? (
          <div>
            <div className="pk-label">Lamination</div>
            <div className="grid grid-cols-3 gap-2">
              {product.lamination_options.map(l => (
                <button key={l.id} onClick={() => { if (requireLogin()) return; setLaminationOpt(l); }} className={`pk-chip justify-between ${laminationOpt?.id === l.id ? 'active' : ''}`} data-testid={`lam-${l.id}`}>
                  <span>{l.label}</span>{l.surcharge ? <span className="font-mono text-[10px]">+₹{l.surcharge}</span> : null}
                </button>
              ))}
            </div>
          </div>
        ) : product.has_lamination && (
          <label className="flex items-center gap-2 pk-card p-3">
            <input type="checkbox" checked={lamination} onChange={e => { if (requireLogin()) return; setLamination(e.target.checked); }} data-testid="lamination-toggle" />
            <div className="flex-1">
              <div className="font-bold text-[13px]">Add lamination</div>
              <div className="text-[11px] text-neutral-500">+₹{product.lamination_surcharge}/unit</div>
            </div>
          </label>
        ))}
      </div>

      {/* Subtotal + proceed */}
      <div className="px-5 mt-6">
        <div className="pk-card p-4 flex items-end justify-between">
          <div>
            <div className="text-[10px] tracking-[0.22em] uppercase text-neutral-500">
              {product.pricing_mode === 'callback' ? 'Custom Quote' : 'Subtotal'}
            </div>
            <div className="font-display text-[28px] mt-1 leading-none font-mono">
              {product.pricing_mode === 'callback' ? 'On Call' : `₹${total.toLocaleString('en-IN')}`}
            </div>
          </div>
          <button onClick={proceed} disabled={product.pricing_mode !== 'callback' && qty < (product.min_qty || 1)} className="pk-btn" data-testid="proceed-checkout">
            {product.pricing_mode === 'callback' ? 'Request Callback' : 'Proceed'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
